import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { Slider, InputNumber, Row, Col, Tooltip, Button, Modal } from "antd";
import { 
  InfoCircleOutlined, 
  BarChartOutlined, 
  HistoryOutlined, 
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
  TrophyOutlined
} from "@ant-design/icons";
import { formatCurrency } from "../../../helpers/retirementGoalCalculations";
import { Line } from "react-chartjs-2";
import { ToolTipCreator } from "../../../helpers/ToolTipCreator";

// Refined Modern Theme - Light Mode with Blue Accents
const COLORS = {
  background: "#f1f5f9",
  cardBg: "#ffffff",
  surface: "#f8fafc",
  text: "#0f172a",
  textSecondary: "#475569",
  primary: "#2a3da3",
  primaryGradient: "linear-gradient(135deg, #2a3da3 0%, #1e293b 100%)",
  success: "#10b981",
  successGradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  warning: "#f59e0b",
  warningGradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  danger: "#ef4444",
  dangerGradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
  border: "#e2e8f0",
  shadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  cardShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
};

// Styled Components
const DashboardContainer = styled.div`
  background: #ffffff;
  width: 100%;
  padding: 40px;
  border-radius: 24px;
  font-family: 'Poppins', 'Inter', sans-serif;
  color: ${COLORS.text};

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 0;
  }
`;

const MainContent = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  overflow-x: hidden;
`;

const TopNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.div`
  h1 {
    font-size: clamp(24px, 5vw, 32px);
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.5px;
    background: linear-gradient(135deg, ${COLORS.primary} 0%, #4f46e5 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  p {
    color: ${COLORS.textSecondary};
    margin: 4px 0 0 0;
    font-size: 14px;
  }
`;

const QuickMetrics = styled.div`
  display: flex;
  gap: 20px;
  background: white;
  padding: 12px 20px;
  border-radius: 14px;
  box-shadow: ${COLORS.shadow};
  border: 1px solid ${COLORS.border};
  width: fit-content;

  @media (max-width: 480px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const QuickStat = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .label {
    font-size: 11px;
    font-weight: 600;
    color: ${COLORS.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }
  .value {
    font-size: 18px;
    font-weight: 700;
    font-family: 'Poppins', sans-serif;
    color: ${props => props.isNegative ? COLORS.danger : COLORS.primary};
  }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${COLORS.cardBg};
  border: 1px solid ${COLORS.border};
  border-top: 4px solid ${props => props.accent || COLORS.primary};
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${COLORS.shadow};
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: ${COLORS.cardShadow};
    transform: translateY(-2px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color + "15"};
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 20px;
`;

const MetricLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${COLORS.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: ${COLORS.text};
  line-height: 1.2;
  margin-bottom: 12px;
  display: flex;
  align-items: baseline;
  
  .unit {
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.textSecondary};
    margin-left: 4px;
  }
`;

const MetricStatus = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.color || COLORS.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid ${COLORS.border};
`;

const VisualizationSection = styled(Card)`
  padding: 32px;
`;

const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  
  h3 {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
  }
`;

const SandboxContainer = styled(Card)`
  background: #ffffff;
  padding: 40px;
  border-radius: 24px;
  border-top: 4px solid ${COLORS.primary}; // Matching the premium top-border theme
`;

const SandboxHeader = styled.div`
  margin-bottom: 32px;
  h3 {
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 8px;
  }
  p {
    color: ${COLORS.textSecondary};
    font-size: 14px;
    margin: 0;
  }
`;

const LeverGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const LeverRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  
  .label-group {
    width: 160px;
    font-weight: 600;
    color: ${COLORS.text};
    font-size: 15px;
    flex-shrink: 0;
  }
  
  .slider-group {
    flex: 1;
    min-width: 100px;
    
    .ant-slider-track {
      background: ${COLORS.primary} !important;
      height: 4px !important;
    }
    .ant-slider-rail {
      height: 4px !important;
      background-color: #f1f5f9 !important;
    }
    .ant-slider-handle {
      width: 16px !important;
      height: 16px !important;
      border: 3px solid ${COLORS.primary} !important;
      background-color: #fff !important;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
      margin-top: -6px !important;
      
      &:hover, &:focus {
        transform: scale(1.1);
        border-color: ${COLORS.primary} !important;
      }

      &::after {
        display: none;
      }
    }
  }
  
  .input-group {
    width: 120px;
    flex-shrink: 0;
    
    .ant-input-number {
      width: 100%;
      height: 44px;
      border-radius: 12px;
      border: 1.5px solid ${COLORS.border};
      background: #fff;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      
      &:hover, &-focused {
        border-color: ${COLORS.primary};
        box-shadow: 0 0 0 3px ${COLORS.primary}15;
      }
      
      .ant-input-number-input {
        height: 100% !important;
        font-weight: 700;
        color: ${COLORS.text};
        text-align: right;
        font-family: 'Poppins', sans-serif;
        font-size: 15px;
        padding-right: 12px;
      }

      .ant-input-number-handler-wrap {
        display: none; // cleaner look without arrows
      }
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    .label-group {
      width: 100%;
    }
    .slider-group {
      width: 100%;
    }
    .input-group {
      width: 100%;
      .ant-input-number {
        height: 40px;
      }
    }
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  margin-top: 16px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    
    button {
      width: 100%;
    }
  }
`;

const StrategyButton = styled(Button)`
  height: 54px;
  padding: 0 32px;
  border-radius: 16px;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s ease;

  &.primary {
    background: ${COLORS.primary};
    color: white;
    border: none;
    box-shadow: 0 8px 16px ${COLORS.primary}30;
    height: 60px; // Slightly taller for better prominence
    padding: 0 40px;
    font-size: 16px;
    
    &:hover {
      background: #1e2c85;
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 12px 24px ${COLORS.primary}40;
      color: white;
    }
  }

  &.outline {
    background: transparent;
    border: 1px solid ${COLORS.border};
    color: ${COLORS.textSecondary};
    
    &:hover {
      border-color: ${COLORS.primary};
      color: ${COLORS.primary};
      background: #fff;
    }
  }
`;

// Helper for formatting large currency
const formatAbbreviated = (val) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
};

// Helper for formatting into full currency ($x,xxx.xx)
const formatFullCurrency = (val) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val || 0);
};

const RetirementStrategyResults = ({
    payload,
    setPayload,
    taxInfo,
    taxInfoAllData,
    rateOfReturnData,
    assetsAssign = [],
    setStrategyDirtyStatus
}) => {
    const defaultValues = useMemo(() => ({
      retireAge: parseFloat(payload["Desired Retirement Age"]) || 65,
      annualSpending: parseFloat(payload["Net Amount Needed in Future with Inflation"]) || parseFloat(payload["Net Annual Pre-Retirement Budget"]) || 60000,
      lifeExpectancy: parseFloat(payload["Client Life Expectancy"]) || 85,
    }), [payload]);

    const [sandbox, setSandbox] = useState({ ...defaultValues });
    const [originalValues] = useState({ ...defaultValues });

    const isDirty = sandbox.retireAge !== originalValues.retireAge || 
                    sandbox.annualSpending !== originalValues.annualSpending || 
                    sandbox.lifeExpectancy !== originalValues.lifeExpectancy;

    useEffect(() => {
      if (setStrategyDirtyStatus) setStrategyDirtyStatus(isDirty);
    }, [isDirty, setStrategyDirtyStatus]);

    const handleSandboxUpdate = (key, value) => {
      setSandbox(prev => ({ ...prev, [key]: value }));
    };

    const [metrics, setMetrics] = useState({
      planConfidence: 0,
      safetyMargin: 0,
      planStatus: "Action Required",
      chartData: null,
      projectedAssets: 0,
      requiredGoal: 0,
      withdrawalYears: 0
    });

    const currentAge = parseFloat(payload["Current Age"]) || 45;

    const runSimulation = (rAge, spending, lifeExp, scenario = "base") => {
      const inflationInput = payload["Inflation rate for this goal"];
      const matchedInflation = taxInfo && taxInfo.find((item) => item.name === inflationInput);
      let inflationRate = matchedInflation ? (parseFloat(matchedInflation.value) || 0) / 100 : 0.03;
      
      const staticReturn = parseFloat(payload["Assumed Annual Investment Return (%)"]) || (rateOfReturnData && rateOfReturnData.staticPercentage) || 6;
      let returnRate = staticReturn / 100;

      if (scenario === "stress") {
        returnRate -= 0.02;
        inflationRate += 0.01;
      } else if (scenario === "optimistic") {
        returnRate += 0.015;
        inflationRate -= 0.005;
      }

      let portfolioBalance = assetsAssign
          .filter(a => a.listType === "liquid")
          .reduce((sum, a) => sum + (parseFloat(a.amountUseForGoal || a.valueOfAssetGoalRate) || 0), 0);
      
      const illiquidAssets = assetsAssign.filter(a => a.listType === "illiquid");

      const ssUser = parseFloat(payload["Your Estimated Social Security"]) || 0;
      const ssSpouse = parseFloat(payload["Social Security - Spouse"]) || 0;
      const pension = parseFloat(payload["Pension"]) || 0;
      const rental = parseFloat(payload["Net Rental Income"]) || 0;

      let additionalIncomes = [];
      if (Array.isArray(payload["Annual Amount"])) {
          additionalIncomes = payload["Annual Amount"];
      } else if (payload["Annual Amount"]) {
          additionalIncomes = [payload["Annual Amount"]];
      }
      const sumAdditionalIncome = additionalIncomes.reduce((s, v) => s + (parseFloat(v) || 0), 0);

      const annualContribution = Array.isArray(assetsAssign) 
          ? assetsAssign.reduce((sum, a) => sum + (parseFloat(a.annualSavings) || 0), 0)
          : (parseFloat(payload["Your Commitment"]) || 0) * 12;

      const results = [];
      let depletedAge = lifeExp + 10;
      let reachedZero = false;
      let balanceAtRetirement = 0;
      const startYear = new Date().getFullYear();

      for (let age = currentAge; age <= 100; age++) {
        const yearPassed = age - currentAge;
        const currentYear = startYear + yearPassed;
        const status = age < rAge ? "Working" : "Retired";

        if (portfolioBalance > 0) portfolioBalance *= (1 + returnRate);
        if (status === "Working") portfolioBalance += annualContribution;

        illiquidAssets.forEach(asset => {
          if (parseInt(asset.saleYear) === currentYear && asset.Strategy === "Sell to Fund") {
            portfolioBalance += parseFloat(asset.netLumpSum) || 0;
          }
        });

        if (status === "Retired") {
          const yearlyExpense = spending * Math.pow(1 + inflationRate, yearPassed);
          const fixedIncomeTotal = (ssUser + ssSpouse + pension + rental + sumAdditionalIncome) * Math.pow(1 + inflationRate, yearPassed);
          portfolioBalance -= (yearlyExpense - fixedIncomeTotal);
        }

        if (age === rAge) balanceAtRetirement = portfolioBalance;

        if (portfolioBalance <= 0 && !reachedZero) {
          depletedAge = age;
          reachedZero = true;
          portfolioBalance = 0;
        }
        results.push({ age, balance: portfolioBalance });
      }

      const yearsInRetirement = Math.max(1, lifeExp - rAge);
      const realRate = (returnRate - inflationRate) / (1 + inflationRate);
      const firstYearShortfall = Math.max(0, spending - (ssUser + ssSpouse + pension + rental + sumAdditionalIncome));
      const firstYearShortfallInflated = firstYearShortfall * Math.pow(1 + inflationRate, rAge - currentAge);
      
      let totalGoalAtRetirement = 0;
      if (realRate !== 0) {
        totalGoalAtRetirement = firstYearShortfallInflated * (1 - Math.pow(1 + realRate, -yearsInRetirement)) / realRate;
      } else {
        totalGoalAtRetirement = firstYearShortfallInflated * yearsInRetirement;
      }

      return { trajectory: results, balanceAtRetirement, depletedAge: reachedZero ? depletedAge : 100, totalGoalAtRetirement };
    };

    useEffect(() => {
      const base = runSimulation(sandbox.retireAge, sandbox.annualSpending, sandbox.lifeExpectancy, "base");
      const stress = runSimulation(sandbox.retireAge, sandbox.annualSpending, sandbox.lifeExpectancy, "stress");
      const opt = runSimulation(sandbox.retireAge, sandbox.annualSpending, sandbox.lifeExpectancy, "optimistic");

      let confidence = Math.round((base.balanceAtRetirement / (base.totalGoalAtRetirement || 1)) * 100);
      let safetyMargin = base.depletedAge - sandbox.lifeExpectancy;
      
      // Synchronize with global calculations if sandbox hasn't changed
      // Also derive the canonical depletion age so the chart zero-crossing
      // matches the Safety Margin tile (both use payload["Funds Last Until Age"]).
      let canonicalDepletionAge = base.depletedAge;
      if (!isDirty) {
        if (payload["Percent Funded"] !== undefined) {
          confidence = Math.round(parseFloat(payload["Percent Funded"]));
        }
        if (payload["Funds Last Until Age"] !== undefined) {
          const fundsLastUntilAge = parseFloat(payload["Funds Last Until Age"]);
          safetyMargin = fundsLastUntilAge - sandbox.lifeExpectancy;
          // Use the payload-sourced age to anchor the chart's depletion point
          canonicalDepletionAge = fundsLastUntilAge;
        }
      }

      // Helper: clamp a trajectory so every data-point at or after the
      // canonical depletion age is shown as 0 (matching what the tiles show).
      const clampTrajectory = (trajectory) => {
        if (canonicalDepletionAge === null) return trajectory;
        return trajectory.map(t =>
          t.age >= canonicalDepletionAge ? { ...t, balance: 0 } : t
        );
      };

      const baseTrajectory   = clampTrajectory(base.trajectory);
      const stressTrajectory = clampTrajectory(stress.trajectory);
      const optTrajectory    = clampTrajectory(opt.trajectory);

      let status = "Action Required";
      let statusColor = COLORS.danger;
      if (confidence >= 95) {
        status = "On Track";
        statusColor = COLORS.success;
      } else if (confidence >= 75) {
        status = "Viable Goal";
        statusColor = COLORS.warning;
      }

      setMetrics({
        planConfidence: confidence,
        safetyMargin: parseFloat(safetyMargin.toFixed(1)),
        planStatus: status,
        statusColor,
        projectedAssets: base.balanceAtRetirement,
        requiredGoal: base.totalGoalAtRetirement,
        withdrawalYears: sandbox.lifeExpectancy - sandbox.retireAge,
        chartData: {
          labels: baseTrajectory.map(t => t.age),
          datasets: [
            {
              label: "Strategy Plan",
              data: baseTrajectory.map(t => t.balance),
              borderColor: "#2A3DA3", // Solid Vibrant Blue
              backgroundColor: "transparent",
              borderWidth: 3, // Reduced from 5 for a cleaner look
              pointRadius: 1,
              pointHoverRadius: 5,
              pointBackgroundColor: "#fff",
              tension: 0.4,
              order: 0
            },
            {
              label: "Stress Case",
              data: stressTrajectory.map(t => t.balance),
              borderColor: "#EF4444", // High-Contrast Red
              backgroundColor: "transparent",
              borderWidth: 1.5,
              borderDash: [5, 5], // Dashed border requested
              pointRadius: 1,
              pointHoverRadius: 5,
              pointBackgroundColor: "#fff",
              tension: 0.4,
              fill: false,
              order: 1
            },
            {
              label: "Optimistic Case",
              data: optTrajectory.map(t => t.balance),
              borderColor: "#10B981", // High-Contrast Green
              backgroundColor: "rgba(42, 61, 163, 0.08)", // Shaded area between cases
              borderWidth: 1.5,
              borderDash: [5, 5], // Dashed border requested
              pointRadius: 1,
              pointHoverRadius: 5,
              pointBackgroundColor: "#fff",
              tension: 0.4,
              fill: '-1', // Fill the area between Optimistic and Stress
              order: 2
            }
          ]
        }
      });
    }, [sandbox, payload, assetsAssign, taxInfo, rateOfReturnData]);

    const handleSave = () => {
      Modal.confirm({
        title: 'Apply Strategy to Plan',
        icon: <TrophyOutlined style={{ color: COLORS.primary }} />,
        content: 'This will update your primary retirement parameters. These changes will reflect across your entire portfolio analysis.',
        okText: 'Confirm & Apply',
        cancelText: 'Cancel',
        okButtonProps: { className: 'primary' },
        onOk: () => {
          setPayload(prev => ({
            ...prev,
            "Desired Retirement Age": sandbox.retireAge,
            "Net Annual Pre-Retirement Budget": sandbox.annualSpending,
            "Client Life Expectancy": sandbox.lifeExpectancy,
            "Plan Confidence": metrics.planConfidence,
            "Retirement Safety Margin": metrics.safetyMargin,
            "Plan Status": metrics.planStatus
          }));
          window.location.reload();
        }
      });
    };

    const displayProjectedAssets = (!isDirty && payload && payload["Projected Value (with current savings)"] !== undefined) 
      ? payload["Projected Value (with current savings)"] 
      : metrics.projectedAssets || 0;
      
    const displayGoalTarget = (!isDirty && payload && payload["Total Savings Goal at Retirement"] !== undefined)
      ? payload["Total Savings Goal at Retirement"] 
      : metrics.requiredGoal || 0;

    return (
        <DashboardContainer>
            <MainContent>
                <TopNav>
                  <HeaderTitle>
                    <h1>Retirement Strategy</h1>
                    <p>Strategize your retirement path with real-time variables.</p>
                  </HeaderTitle>
                  <QuickMetrics>
                    <QuickStat isNegative={displayProjectedAssets < 0}>
                      <div className="label">Projected Assets</div>
                      <div className="value">
                        {displayProjectedAssets < 0 ? '-' : ''}
                        {formatFullCurrency(Math.abs(displayProjectedAssets))}
                      </div>
                    </QuickStat>
                    <div style={{ width: 1, backgroundColor: COLORS.border, height: 24, alignSelf: 'center' }} />
                    <QuickStat>
                      <div className="label">Goal Target</div>
                      <div className="value">{formatFullCurrency(displayGoalTarget)}</div>
                    </QuickStat>
                  </QuickMetrics>
                </TopNav>

                <MetricGrid>
                    <Card accent={COLORS.primary}>
                        <CardIcon color={COLORS.primary}><BarChartOutlined /></CardIcon>
                        <MetricLabel>
                            Plan Confidence
                            <ToolTipCreator name="retirement_plan_confidence" />
                        </MetricLabel>
                        <MetricValue>
                            {metrics.planConfidence}<span className="unit">%</span>
                        </MetricValue>
                        <MetricStatus color={COLORS.primary}>
                           Analysis based on current assets
                        </MetricStatus>
                    </Card>

                    <Card accent={metrics.safetyMargin >= 0 ? COLORS.success : COLORS.danger}>
                        <CardIcon color={metrics.safetyMargin >= 0 ? COLORS.success : COLORS.danger}><HistoryOutlined /></CardIcon>
                        <MetricLabel>
                            Safety Margin
                            <ToolTipCreator name="retirement_safety_margin" />
                        </MetricLabel>
                        <MetricValue>
                          {metrics.safetyMargin >= 0 ? '+' : ''}{metrics.safetyMargin}<span className="unit">yrs</span>
                        </MetricValue>
                        <MetricStatus color={metrics.safetyMargin >= 0 ? COLORS.success : COLORS.danger}>
                            {metrics.safetyMargin >= 0 ? <CheckCircleOutlined /> : <WarningOutlined />}
                            {metrics.safetyMargin >= 0 ? "Projected Buffer" : "Projected Shortfall"}
                        </MetricStatus>
                    </Card>

                    <Card accent={metrics.statusColor}>
                        <CardIcon color={metrics.statusColor}><TrophyOutlined /></CardIcon>
                        <MetricLabel>
                            Plan Health
                            <ToolTipCreator name="retirement_plan_health" />
                        </MetricLabel>
                        <MetricValue style={{ fontSize: 26 }}>
                          {metrics.planStatus}
                        </MetricValue>
                        <MetricStatus color={metrics.statusColor}>
                            Evaluated at {sandbox.lifeExpectancy} yrs old
                        </MetricStatus>
                    </Card>
                </MetricGrid>

                <VisualizationSection>
                  <AnalyticsHeader>
                    <h3>Wealth Path Trajectory</h3>
                    <div style={{ display: 'flex', gap: 24 }}>
                       <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: COLORS.textSecondary }}>
                          <div style={{ width: 14, height: 4, background: "#2A3DA3", borderRadius: 2 }} title="Primary strategy path" /> Strategy Plan
                       </div>
                       <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: COLORS.textSecondary }}>
                          <div style={{ width: 14, height: 8, background: "rgba(42, 61, 163, 0.15)", borderLeft: "2px dashed #10B981", borderRight: "2px dashed #EF4444", borderRadius: 1 }} /> Confidence Area
                       </div>
                    </div>
                  </AnalyticsHeader>
                  <div style={{ height: 320 }}>
                    {metrics.chartData && (
                      <Line 
                        data={metrics.chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                           tooltips: {
                            callbacks: {
                              label: function (tooltipItem, chartData) {
                                const datasetLabel =
                                  chartData.datasets[tooltipItem.datasetIndex].label || "";
                                const value = tooltipItem.yLabel;
                                const formattedValue = new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(value);
                                return `${datasetLabel}: ${formattedValue}`;
                              },
                            },
                          },
                          interaction: {
                            mode: 'index',
                            intersect: false,
                          },
                          scales: {
                            x: { grid: { display: false }, ticks: { color: COLORS.textSecondary, font: { family: 'Poppins', size: 11 } } },
                            y: { grid: { color: "#f1f5f9" }, border: { display: false }, ticks: { color: COLORS.textSecondary, font: { family: 'Poppins', size: 11 }, callback: (v) => formatFullCurrency(v) } },
                             yAxes: [
                                {
                                  ticks: {
                                    beginAtZero: true,
                                    callback: function (value) {
                                      return new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }).format(value);
                                    },
                                  },
                                },
                              ],
                          },
                          plugins: { 
                            legend: { display: false },
                            tooltip: {
                              backgroundColor: '#1e293b',
                              padding: 12,
                              titleFont: { size: 14, weight: 'bold' },
                              bodyFont: { size: 13 },
                              displayColors: false,
                              callbacks: {
                                label: function(context) {
                                  let label = context.dataset.label || '';
                                  if (label) {
                                    label += ': ';
                                  }
                                  if (context.parsed.y !== null) {
                                    label += formatFullCurrency(context.parsed.y);
                                  }
                                  return label;
                                }
                              }
                            }
                          }
                        }}
                      />
                    )}
                  </div>
                </VisualizationSection>

                <SandboxContainer>
                    <SandboxHeader>
                        <h3>Strategy Playground</h3>
                        <p>Adjust your core parameters to see how small changes impact your long-term wealth.</p>
                    </SandboxHeader>
                    <LeverGrid>
                        <LeverRow>
                            <div className="label-group">Retirement Age</div>
                            <div className="slider-group">
                                <Slider 
                                    min={currentAge} max={90} 
                                    value={sandbox.retireAge} 
                                    onChange={v => handleSandboxUpdate('retireAge', v)} 
                                />
                            </div>
                            <div className="input-group">
                                <InputNumber 
                                    min={currentAge} max={90}
                                    value={sandbox.retireAge} 
                                    onChange={v => handleSandboxUpdate('retireAge', v)} 
                                />
                            </div>
                        </LeverRow>

                        <LeverRow>
                            <div className="label-group">Annual Spending</div>
                            <div className="slider-group">
                                <Slider 
                                    min={originalValues.annualSpending * 0.5} 
                                    max={originalValues.annualSpending * 1.5} 
                                    step={1000}
                                    value={sandbox.annualSpending} 
                                    onChange={v => handleSandboxUpdate('annualSpending', v)} 
                                />
                            </div>
                            <div className="input-group">
                                <InputNumber 
                                    min={originalValues.annualSpending * 0.5}
                                    max={originalValues.annualSpending * 1.5}
                                    value={sandbox.annualSpending} 
                                    onChange={v => handleSandboxUpdate('annualSpending', v)}
                                    formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={v => v.replace(/\$\s?|(\,*)/g, '')}
                                />
                            </div>
                        </LeverRow>

                        <LeverRow>
                            <div className="label-group">Life Expectancy</div>
                            <div className="slider-group">
                                <Slider 
                                    min={sandbox.retireAge + 1} max={110} 
                                    value={sandbox.lifeExpectancy} 
                                    onChange={v => handleSandboxUpdate('lifeExpectancy', v)} 
                                />
                            </div>
                            <div className="input-group">
                                <InputNumber 
                                    min={sandbox.retireAge + 1} max={110}
                                    value={sandbox.lifeExpectancy} 
                                    onChange={v => handleSandboxUpdate('lifeExpectancy', v)} 
                                />
                            </div>
                        </LeverRow>
                    </LeverGrid>

                    <FooterActions>
                        {isDirty && (
                          <StrategyButton className="outline" onClick={() => setSandbox({ ...originalValues })}>
                            Discard Adjustments
                          </StrategyButton>
                        )}
                        <StrategyButton 
                          className="primary" 
                          onClick={handleSave} 
                          disabled={!isDirty}
                        >
                          Save Strategy to Plan <ArrowRightOutlined />
                        </StrategyButton>
                    </FooterActions>
                </SandboxContainer>

                {isDirty && (
                  <div style={{ textAlign: 'right', color: COLORS.warning, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    <WarningOutlined /> Strategy adjustments have not been saved.
                  </div>
                )}
            </MainContent>
        </DashboardContainer>
    );
};

export default RetirementStrategyResults;
